import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { BuildingProject } from "@/types/projects";
import type { Translations } from "@/lib/i18n/types";

/**
 * Meta line (doc count + last update) for ProjectOptionRow, used by
 * WorkInProjectDialog (chat "Work in a project" picker, WHA-3829). Kept out
 * of ProjectOptionRow.tsx so that file only exports components
 * (react-refresh).
 */

// Per-project live document counts for the meta line, one round trip for the
// whole building.
export const useProjectDocCounts = (buildingId: string) => {
  const { data } = useQuery({
    queryKey: ["building-documents-per-project", buildingId],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_building_documents_per_project", {
        p_building_id: Number(buildingId),
      });
      if (error) throw error;
      // The exported client is untyped (see CLAUDE.md), so type the rows here.
      const rows = (data ?? []) as Array<{ project_id: number; doc_count: number }>;
      return new Map(rows.map((row) => [String(row.project_id), row.doc_count]));
    },
    enabled: !!buildingId,
    staleTime: 60_000,
  });
  return data;
};

// "24 documents · Updated by Mari · 29 May" — parts drop out when unknown.
export const projectMeta = (
  project: BuildingProject,
  docCount: number | undefined,
  t: Translations,
  locale: string
) => {
  const parts: string[] = [];
  if (docCount != null) parts.push(`${docCount} ${t.projects.selector.documentsLabel}`);
  const editorName = project.modified_by_name ?? project.created_by_name;
  if (editorName) parts.push(`${t.projects.selector.updatedBy} ${editorName}`);
  parts.push(
    new Date(project.modified_at || project.created_at).toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
    })
  );
  return parts.join(" · ");
};
